// src/components/ImageLightbox.js
import React, { useEffect, useState, useCallback } from 'react';
import '../styles/SuitcaseTheatreArcadeStyle.css';

export default function ImageLightbox({ src, alt = '', onClose }) {
    const [isZoomed, setIsZoomed] = useState(false);
    const [origin, setOrigin] = useState({ x: 50, y: 50 });

    // Lock body scroll while open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // ESC to close
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const toggleZoom = useCallback((e) => {
        e.stopPropagation();
        setIsZoomed(z => !z);
    }, []);

    const setOriginFromPoint = useCallback((el, x, y) => {
        const r = el.getBoundingClientRect();
        const ox = ((x - r.left) / r.width) * 100;
        const oy = ((y - r.top) / r.height) * 100;
        setOrigin({ x: ox, y: oy });
    }, []);

    const onMouseMove = (e) => {
        if (!isZoomed) return;
        setOriginFromPoint(e.currentTarget, e.clientX, e.clientY);
    };

    // Touch: double-tap to zoom, move finger to pan focus
    const [lastTap, setLastTap] = useState(0);
    const onTouchStart = (e) => {
        const now = Date.now();
        if (now - lastTap < 300) setIsZoomed(z => !z);
        setLastTap(now);
    };
    const onTouchMove = (e) => {
        if (!isZoomed) return;
        const t = e.touches[0];
        if (!t) return;
        setOriginFromPoint(e.currentTarget, t.clientX, t.clientY);
    };

    return (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={onClose}>
            <button
                className="lightbox-close"
                aria-label="Close"
                onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                title="Close"
            >
                ✕
            </button>

            <div
                className="lightbox-content"
                onClick={(e) => e.stopPropagation()}
                onMouseMove={onMouseMove}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
            >
                <img
                    src={src}
                    alt={alt}
                    className="lightbox-image"
                    onClick={toggleZoom}
                    style={{
                        transform: isZoomed ? 'scale(2)' : 'scale(1)',
                        transformOrigin: `${origin.x}% ${origin.y}%`,
                        transition: 'transform 0.25s ease, transform-origin 0.25s ease',
                        cursor: isZoomed ? 'move' : 'zoom-in',
                        maxHeight: '90vh',
                        objectFit: 'contain'
                    }}
                />
            </div>
        </div>
    );
}
